import { useTranslation } from "react-i18next";
import {
  brandIconPaths,
  buIconLabelKey,
  isStarterIcon,
  normalizeBuIconId,
} from "@/utils/buIcons";
import { useBrandIconLibrary } from "@/utils/useBrandIconLibrary";
import { cn } from "@/utils/cn";

/**
 * A business unit's icon, wherever the unit is named (NAV-103, NAV-116).
 *
 * The one place a stored value becomes a picture, so no two screens can mean
 * different things by the same string.
 *
 * **Mount it wherever a unit is named.** That is the load-bearing half: a picker
 * that stores the right value is worth nothing if the surfaces beside a unit's
 * name draw something fixed instead, and a reader cannot tell that apart from a
 * save that did not take. The sites today are the admin header's context chip,
 * the account menu, the reader's own settings page, the catalogue's
 * editors'-picks heading, the super admin's business-unit list and the picker
 * itself. A new surface that names a unit belongs on that list.
 *
 * `currentColor`, because the library is drawn in one colour and captioned
 * "these icons can be individually recolored to fit your needs" — so it takes
 * the navy of whatever it sits in, light theme and dark alike, rather than
 * carrying a hex of its own.
 */
export function BusinessUnitIcon({
  icon,
  className,
  /** Hidden from assistive technology when the unit's name is already beside
   *  it, which is the usual case: the icon repeats the name rather than adding
   *  to it, and announcing "book, Macmillan" twice helps nobody. */
  decorative = true,
}: {
  icon: string | null | undefined;
  className?: string;
  decorative?: boolean;
}) {
  const { t } = useTranslation(["buIcons"]);
  const resolved = normalizeBuIconId(icon);
  const paths = useBrandIconPaths(resolved);
  const label = t(buIconLabelKey(resolved));

  return (
    <svg
      viewBox="0 0 240 240"
      className={cn("h-6 w-6", className)}
      fill="currentColor"
      role={decorative ? "presentation" : "img"}
      aria-hidden={decorative || undefined}
      aria-label={decorative ? undefined : label}
      data-testid={`bu-icon-${resolved}`}
    >
      {paths?.map((d) => (
        <path key={d} fillRule="evenodd" d={d} />
      ))}
    </svg>
  );
}

/**
 * One icon's geometry, fetching the library half if that is where it lives.
 *
 * Renders as an empty box of the right size until the chunk arrives, never as a
 * *different* icon — see `brandIconPaths` for why that distinction is the point
 * rather than a nicety. The box keeps its space, so nothing reflows when the
 * drawing appears.
 */
export function useBrandIconPaths(
  icon: ReturnType<typeof normalizeBuIconId>,
): readonly string[] | undefined {
  // The return value is read for its side effect on rendering, not its value:
  // the paths live in module state, and this is how a component learns they
  // arrived.
  useBrandIconLibrary(!isStarterIcon(icon));
  return brandIconPaths(icon);
}
